/* eslint-disable sort-keys */
import { useEffect, useRef } from 'react';

import { useAtomValue, useSetAtom, useStore } from 'jotai';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  redirect,
  RouterProvider,
  useLocation,
  useSearchParams,
} from 'react-router-dom';

import {
  authHelpers,
  cookieJar,
  isValidFlowId,
  uiRoutes,
} from '@consumer-frontend/api';
import {
  accountIdAtom,
  AlertBanner,
  clearFlowStorage,
  CnuFooter,
  CnuHeader,
  CnuLoadingSpinner,
  CnuMaintenancePage,
  contractStepAtom,
  Error,
  flowIdAtom,
  leaveContractApp,
  LoadingCard,
  orchestratorAccountIdAtom,
  useLoadLoanOffer,
} from '@consumer-frontend/components';
import { useFeatureFlag } from '@consumer-frontend/optimizely';
import { initFraudForce } from '@consumer-frontend/shared/iovation/iovation-fraud-force';

import { ContractGondola } from './ContractGondola/ContractGondola';
import { ContractStepRoute } from './ContractStepRoute';
import { analytics, identifyCustomer } from '../utils/analytics';

import styles from './App.module.scss';

export const appRedirects = {
  toAccountHome: () => {
    window.location.href = uiRoutes.accountHome;
  },
  toLogin: () => {
    authHelpers.logOutAndRedirect('no_session');
  },
};

export const RootLayout = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const store = useStore();
  const flowId = searchParams.get('flowId') || '';
  const storedFlowId = useAtomValue(flowIdAtom);
  const setFlowId = useSetAtom(flowIdAtom);
  const effectiveFlowId = (isValidFlowId(flowId) ? flowId : '') || storedFlowId;
  // flowId arrives on the URL, so it's customer-suppliable — a crafted `?flowId=../../foo`
  // would otherwise ride along into the orchestrator endpoint paths this atom feeds (see
  // apiEndpoints in libs/api). Validating the shape here, before it's ever stashed or used to
  // fetch anything, is belt-and-braces alongside those endpoints encoding it.
  const isFlowIdValid = isValidFlowId(effectiveFlowId);
  // Tracks whether this mount has ever had a valid flowId. clearFlowStorage resets flowIdAtom
  // to '' as part of leaving the app (see leaveContractApp) *before* the window.location.href
  // assignment that follows it actually takes effect, so this component can re-render with
  // isFlowIdValid now false while a redirect (e.g. to contractSubmitted or loginInactivity) is
  // still pending. Without this, the "no flow to resume" effect below would treat that as a
  // fresh missing flowId and overwrite the pending redirect with one to account home instead.
  const hadValidFlowId = useRef(false);

  if (isFlowIdValid) hadValidFlowId.current = true;

  // The layout owns the fetch because the offer decides the step list, so it owns the states
  // where there is no step list to render: without this a failed offer is a blank page.
  const {
    hasNoContractOffer,
    isError,
    isLoading,
  } = useLoadLoanOffer(isFlowIdValid ? effectiveFlowId : '');
  const steps = useAtomValue(contractStepAtom);

  const isLoggedOut = !cookieJar.hasSession();

  useEffect(() => {
    if (isLoggedOut) {
      appRedirects.toLogin();

      return;
    }

    if (isFlowIdValid && hasNoContractOffer) {
      appRedirects.toAccountHome();
    }
  }, [isLoggedOut, isFlowIdValid, hasNoContractOffer]);

  const jwtAccountId = useAtomValue(accountIdAtom);
  const orchestratorAccountId = useAtomValue(orchestratorAccountIdAtom);
  const customerId = jwtAccountId || orchestratorAccountId;

  useEffect(() => {
    identifyCustomer(customerId);
  }, [customerId]);

  useEffect(() => {
    analytics.track('routingChange', {
      to_path: location.pathname,
    });
  }, [location.pathname]);

  // Internal navigation (e.g. ContractIndexRedirect's redirect to the first step) doesn't
  // carry `?flowId=...` forward, so stash it once here for the rest of the flow to read.
  useEffect(() => {
    if (!flowId || !isValidFlowId(flowId)) return;

    // A new flowId showing up while a different one is already stashed means the customer
    // returned to this tab (e.g. the back button) and started a different flow — the previous
    // flow's contracts, step progress, draw input, and payment method must not carry over
    // into it.
    if (storedFlowId && storedFlowId !== flowId) {
      clearFlowStorage(store.set);
    }

    setFlowId(flowId);
  }, [flowId, storedFlowId, store, setFlowId]);

  // Fire-and-forget, kicked off once as early as the flow loads rather than waiting until
  // LocAuthPage's final submit reads the fingerprint — see getFingerprint's own doc comment:
  // FraudForce needs time after its scripts run to compute a blackbox, so starting the load
  // here gives it the whole Funds+Auth flow to finish before that submit needs the result.
  // initFraudForce never rejects (failures are caught and reported internally), and nothing
  // here needs its result, so there's nothing further to do with it.
  useEffect(() => {
    initFraudForce();
  }, []);

  // No flowId anywhere, or one that doesn't look like a real flowId, means there's no flow to
  // resume — send the customer to account home rather than leaving them on a page that can
  // never load (or, for a malformed one, letting it reach an orchestrator request at all).
  useEffect(() => {
    if (!isFlowIdValid && !hadValidFlowId.current) {
      leaveContractApp(store.set, uiRoutes.accountHome);
    }
  }, [isFlowIdValid, store]);

  // The header/footer's own links (Link, from shared-ui) point to other cnufe pages — a real
  // browser navigation, not an SPA route change, since react-router only intercepts same-app
  // links. That's leaving this app the same way any of leaveContractApp's destinations are, so
  // the sensitive parts of what's persisted for a refresh (the loan offer summary, the contract
  // documents) get the same cleanup here — a capture-phase listener, since the click itself isn't
  // otherwise observable, and synchronous, since sessionStorage has to be cleared before the
  // browser's default navigation actually happens. A target="_blank" link (e.g. the footer's
  // social icons) opens a new tab without leaving this one, so it's excluded.
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      // A modifier/middle click opens the link in a new tab or window instead of leaving this
      // one — this tab's in-progress flow shouldn't be wiped for that.
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }

      const anchor = (event.target as HTMLElement | null)?.closest('a');

      if (!anchor || anchor.target === '_blank') return;

      const href = anchor.getAttribute('href');

      if (!href || href.startsWith('/contract') || href.startsWith('#')) return;

      // tel:/mailto:/sms: hand off to the OS or a phone/mail app rather than the browser
      // navigating this document anywhere — the footer's phone number is one of these. The tab
      // (and its flow) stays right where it is, so there's nothing to clean up for.
      if (/^(tel|mailto|sms|fax):/i.test(href)) return;

      // Deferred to a microtask: this listener runs in capture phase, ahead of the browser's
      // own default navigation — but that also puts it ahead of any other click handler
      // further down the dispatch, including one that calls preventDefault() to cancel the
      // navigation outright. Checking defaultPrevented only after the full synchronous
      // dispatch has finished (a microtask still runs before the browser acts on it) is what
      // actually tells "navigating away" apart from "click was cancelled, tab stays put."
      queueMicrotask(() => {
        if (event.defaultPrevented) return;

        clearFlowStorage(store.set);
      });
    };

    document.addEventListener('click', handleClick, true);

    return () => document.removeEventListener('click', handleClick, true);
  }, [store]);

  const renderStep = () => {
    if (!isFlowIdValid) {
      return <LoadingCard />;
    }

    if (isLoggedOut || hasNoContractOffer || isLoading) {
      return <CnuLoadingSpinner />;
    }

    if (isError && (!isLoading && !steps.length)) {
      return <Error message="There was an issue loading your contract details." />;
    }

    return <Outlet />;
  };

  return (
    <>
      <CnuHeader
        centeredLogo
        isBlocCustomer={false}
        logoUri="/assets/images/CNU_Logo.svg"
        renderNav={false}
      />
      <div className={styles.content}>
        <AlertBanner />
        {renderStep()}
      </div>
      <CnuFooter />
      <ContractGondola />
    </>
  );
};

const ContractIndexRedirect = () => {
  const steps = useAtomValue(contractStepAtom);

  if (!steps.length) {
    return <LoadingCard />;
  }

  return <Navigate replace to={`/contract/${steps[0].key}`} />;
};

let router: ReturnType<typeof createBrowserRouter> | undefined;

// Built on first use rather than at module scope. createBrowserRouter self-initializes:
// it matches the current URL and runs that route's loader as soon as it is constructed,
// without waiting for RouterProvider to mount. At module scope that meant the '/' ->
// '/contract' redirect fired even while the maintenance gate was rendering, rewriting
// the address bar during an outage. Deferring construction past the gate keeps it
// genuinely inert, and memoizing keeps a single instance across re-renders.
const getRouter = () => {
  router ??= createBrowserRouter([
    {
      path: '/',
      // Carries `?flowId=...` forward: a plain `redirect('/contract')` drops the query
      // string, and RootLayout only reads flowId off the URL on this very first load.
      loader: ({ request }) => redirect(`/contract${new URL(request.url).search}`),
    },
    {
      path: '/contract',
      element: <RootLayout />,
      children: [
        {
          path: '',
          index: true,
          element: <ContractIndexRedirect />,
        },
        // Every step renders through here; StepRoute resolves :step to a page.
        {
          path: ':step',
          element: <ContractStepRoute />,
        },
      ],
    },
  ]);

  return router;
};

export const App = () => {
  const { enabled, isLoading } = useFeatureFlag('cnu_maintenance');

  // Nothing renders until the decision lands, so the app never flashes before
  // being replaced by the maintenance page.
  if (isLoading) {
    return null;
  }

  // Returning before getRouter() is what makes this cover every route: no router
  // exists to match a URL or run a loader, so the '/' redirect and RootLayout's
  // offer fetch are both skipped while the flag is on.
  if (enabled) {
    return <CnuMaintenancePage />;
  }

  return <RouterProvider router={getRouter()} />;
};
